// src/components/SEO/SEOHead.tsx
import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  author?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
}

export default function SEOHead({
  title = "Aurore RH | Consultante RH & Droit Social",
  description = "Consultante RH & droit social pour entreprises, collectivités et particuliers. Accompagnement personnalisé en recrutement, formation et gestion des conflits.",
  canonical = "https://aurorerh.fr/",
  ogImage = "https://aurorerh.fr/og-image.png",
  keywords = "consultant RH, droit social, accompagnement RH, gestion des conflits, formation management, recrutement, audit RH, collectivités territoriales, coaching professionnel",
  author = "Aurore RH",
  type = "website",
  noindex = false
}: SEOHeadProps) {
  
  useEffect(() => {
    // Title
    document.title = title;
    
    // Meta tags dynamiques
    const metaTags: Record<string, string> = {
      'description': description,
      'keywords': keywords,
      'author': author,
      'robots': noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
      'googlebot': noindex ? 'noindex, nofollow' : 'index, follow',
      'bingbot': noindex ? 'noindex, nofollow' : 'index, follow',
      
      // Open Graph
      'og:title': title,
      'og:description': description,
      'og:url': canonical,
      'og:image': ogImage,
      'og:image:width': '1200',
      'og:image:height': '630',
      'og:type': type,
      'og:site_name': 'Aurore RH',
      'og:locale': 'fr_FR',
      
      // Twitter Card
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage,
      
      // Mobile
      'mobile-web-app-capable': 'yes',
      'apple-mobile-web-app-capable': 'yes',
      'apple-mobile-web-app-status-bar-style': 'black-translucent',
      'apple-mobile-web-app-title': 'Aurore RH',
      'theme-color': '#C5604E',
      
      // SEO avancé
      'revisit-after': '7 days',
      'language': 'French',
      'geo.region': 'FR',
      'geo.placename': 'France',
      
      // Préférences utilisateur
      'color-scheme': 'light',
      
      // Security & Privacy
      'referrer': 'strict-origin-when-cross-origin'
    };
    
    Object.entries(metaTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    
    // Canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
    
    // Alternate language
    let alternateLang = document.querySelector('link[rel="alternate"][hreflang="fr"]');
    if (!alternateLang) {
      alternateLang = document.createElement('link');
      alternateLang.setAttribute('rel', 'alternate');
      alternateLang.setAttribute('hreflang', 'fr');
      document.head.appendChild(alternateLang);
    }
    alternateLang.setAttribute('href', canonical);
    
    // Preconnect pour performances
    const preconnects = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://assets.calendly.com'
    ];
    
    preconnects.forEach(url => {
      if (!document.querySelector(`link[rel="preconnect"][href="${url}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        if (url.includes('gstatic')) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      }
    });
    
  }, [title, description, canonical, ogImage, keywords, author, type, noindex]);
  
  return null;
}
